import { Box, Tab, Tabs, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import React, { useState, useEffect } from 'react';
import { ListBase, useListContext } from 'react-admin';
import LocationTable from './locations/LocationTable';
import CrosswalkFilters from './CrosswalkFilters';
import CreateLocationDialog from './locations/CreateLocationDialog';
import BulkAddLocationsDialog from './locations/BulkAddLocationsDialog';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MedicalGroupsTable from './medical/MedicalGroupsTable';
import CreateMedicalGroupDialog from './medical/CreateMedicalDialog';
import BulkAddMedicalGroupsDialog from './medical/BulkAddMedicalGroupsDialog';
import DivisionsTable from './divisions/DivisionsTable';
import CreateDivisionDialog from './divisions/CreateDivisionDialog';
import BulkAddDivisionsDialog from './divisions/BulkAddDivisionsDialog';

interface Client {
  billing_client_number: string;
}

const CrosswalksLayout = ({
  activeTab,
  setActiveTab,
}: {
  activeTab: number;
  setActiveTab: (index: number) => void;
}) => {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientNumber, setClientNumber] = useState('');

  const [isCreateLocationDialogOpen, setCreateLocationDialogOpen] = useState(false);
  const [isBulkAddLocationOpen, setBulkAddLocationOpen] = useState(false);

  const [isCreateMedicalDialogOpen, setCreateMedicalDialogOpen] = useState(false);
  const [isBulkAddMedicalOpen, setBulkAddMedicalOpen] = useState(false);

  const [isCreateDivisionDialogOpen, setCreateDivisionDialogOpen] = useState(false);
  const [isBulkAddDivisionOpen, setBulkAddDivisionOpen] = useState(false);

  const [shouldReset, setShouldReset] = useState(false);

  const { setFilters, filterValues, setSort } = useListContext();

  const isActionDisabled = !selectedClient;

  useEffect(() => {
    setShouldReset(true);
    const timer = setTimeout(() => setShouldReset(false), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setClientNumber(selectedClient ? selectedClient.billing_client_number : '');
  }, [selectedClient]);

  useEffect(() => {
    if (filterValues?.billing_client_number && filterValues?.billing_client_name) {
      setSelectedClient({
        billing_client_number: filterValues.billing_client_number,
      });
    }
  }, []);

  useEffect(() => {
    if (selectedClient?.billing_client_number !== filterValues.billing_client_number) {
      setFilters({
        billing_client_number: selectedClient?.billing_client_number,
      });
    }
  }, [selectedClient]);

  // Set default sorting when the component mounts or when the active tab changes
  useEffect(() => {
    setSort({ field: 'billing_client_number', order: 'ASC' });
  }, [activeTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleApplyFilters = () => {
    const filters = {
      billing_client_number: clientNumber,
    };
    setFilters(filters);
  };

  const handleClearFilters = () => {
    setSelectedClient(null);
    setClientNumber('');
    setFilters({});
  };

  const handleCreateSave = async (newLocationData: any) => {
    setCreateLocationDialogOpen(false);
    setCreateMedicalDialogOpen(false);
    setCreateDivisionDialogOpen(false);
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <CrosswalkFilters
        selectedClient={selectedClient}
        onClientChange={setSelectedClient}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        setSelectedClient={setSelectedClient}
        reset={shouldReset}
      />

      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="crosswalk tables tabs">
            <Tab label="Medical" />
            <Tab label="Locations" />
            <Tab label="Divisions" />
          </Tabs>
          {(activeTab === 0 || activeTab === 1 || activeTab === 2) && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeTab === 0 ? (
                <>
                  <Button
                    variant="contained"
                    onClick={() => setCreateMedicalDialogOpen(true)}
                    startIcon={<AddIcon />}
                    disabled={isActionDisabled}
                  >
                    Create
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setBulkAddMedicalOpen(true)}
                    startIcon={<UploadFileIcon />}
                    disabled={isActionDisabled}
                  >
                    Import
                  </Button>
                </>
              ) : activeTab === 1 ? (
                <>
                  <Button
                    variant="contained"
                    onClick={() => setCreateLocationDialogOpen(true)}
                    startIcon={<AddIcon />}
                    disabled={isActionDisabled}
                  >
                    Create
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setBulkAddLocationOpen(true)}
                    startIcon={<UploadFileIcon />}
                    disabled={isActionDisabled}
                  >
                    Import
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={() => setCreateDivisionDialogOpen(true)}
                    startIcon={<AddIcon />}
                    disabled={isActionDisabled}
                  >
                    Create
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setBulkAddDivisionOpen(true)}
                    startIcon={<UploadFileIcon />}
                    disabled={isActionDisabled}
                  >
                    Import
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
        <Box mt={2}>
          {activeTab === 0 && <MedicalGroupsTable />}
          {activeTab === 1 && <LocationTable />}
          {activeTab === 2 && <DivisionsTable />}
        </Box>
      </Box>

      {isCreateLocationDialogOpen && (
        <CreateLocationDialog
          isOpen={isCreateLocationDialogOpen}
          onClose={() => setCreateLocationDialogOpen(false)}
          onSave={handleCreateSave}
          selectedClient={selectedClient}
        />
      )}

      {isBulkAddLocationOpen && (
        <BulkAddLocationsDialog
          isOpen={isBulkAddLocationOpen}
          onClose={() => setBulkAddLocationOpen(false)}
          selectedClient={selectedClient}
        />
      )}

      {isCreateMedicalDialogOpen && (
        <CreateMedicalGroupDialog
          isOpen={isCreateMedicalDialogOpen}
          onClose={() => setCreateMedicalDialogOpen(false)}
          onSave={handleCreateSave}
          selectedClient={selectedClient}
        />
      )}

      {isBulkAddMedicalOpen && (
        <BulkAddMedicalGroupsDialog
          isOpen={isBulkAddMedicalOpen}
          onClose={() => setBulkAddMedicalOpen(false)}
          selectedClient={selectedClient}
        />
      )}

      {isCreateDivisionDialogOpen && (
        <CreateDivisionDialog
          isOpen={isCreateDivisionDialogOpen}
          onClose={() => setCreateDivisionDialogOpen(false)}
          onSave={handleCreateSave}
          selectedClient={selectedClient}
        />
      )}

      {isBulkAddDivisionOpen && (
        <BulkAddDivisionsDialog
          isOpen={isBulkAddDivisionOpen}
          onClose={() => setBulkAddDivisionOpen(false)}
          selectedClient={selectedClient}
        />
      )}
    </Box>
  );
};

const Crosswalks = () => {
  const [activeTab, setActiveTab] = useState(0);

  const resources = ['MedicalGroupsCrosswalk', 'LocationsCrosswalk', 'DivisionsCrosswalk'];

  return (
    <ListBase
      resource={resources[activeTab]}
      perPage={10}
      sort={{ field: 'billing_client_number', order: 'ASC' }}
    >
      <CrosswalksLayout activeTab={activeTab} setActiveTab={setActiveTab} />
    </ListBase>
  );
};

export default Crosswalks;
